// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useSavingGoalsStore } from '@/store/savingGoalsStore'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Building2, Calculator, CalendarIcon, Target, AlertCircle, Trash2, X } from 'lucide-react'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Textarea } from '@/components/ui/textarea'
import { formatCurrency } from '@/utils/formatters'
import { useCurrenciesStore } from '@/store/currenciesStore'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { AccountType } from '@/types/account'

interface ExtendedSavingGoal {
  id: string;
  user_id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  currency: string;
  target_date: string | null;
  account_id: string;
  icon: string | null;
  color: string | null;
  description: string | null;
  status?: 'active' | 'paused' | 'completed';
  is_completed: boolean;
  created_at: string;
  updated_at: string;
  account?: {
    id: string;
    name: string;
    type: AccountType;
    balance: number;
    currency?: string;
  };
}

interface SimplifiedAccount {
  id: string;
  name: string;
  type: AccountType;
  balance: number;
  currency?: string;
}

interface EditSavingGoalDialogProps {
  goal: ExtendedSavingGoal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savingsAccounts: SimplifiedAccount[];
}

export function EditSavingGoalDialog({ goal, open, onOpenChange, savingsAccounts }: EditSavingGoalDialogProps) {
  const { user } = useAuth()
  const { currencies } = useCurrenciesStore()
  const currencySymbol = currencies.find(c => c.isDefault)?.symbol || '$'
  const { updateGoal, deleteGoal } = useSavingGoalsStore()
  const [loading, setLoading] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [formData, setFormData] = useState({
    name: goal.name,
    target_amount: goal.target_amount.toString(),
    description: goal.description || '',
    target_date: goal.target_date ? new Date(goal.target_date) : new Date()
  })

  const [validationError, setValidationError] = useState<string | null>(null)
  const [selectedAccountId, setSelectedAccountId] = useState(goal.account_id)

  const suggestedSavings = formData.target_amount
    ? {
        daily: parseFloat(formData.target_amount) / 30,
        weekly: parseFloat(formData.target_amount) / 4,
        monthly: parseFloat(formData.target_amount)
      }
    : null

  // Update form when goal changes
  useEffect(() => {
    setFormData({
      name: goal.name,
      target_amount: goal.target_amount.toString(),
      description: goal.description || '',
      target_date: goal.target_date ? new Date(goal.target_date) : new Date()
    })
    setSelectedAccountId(goal.account_id)
  }, [goal])

  // Handle dialog state changes
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      // Reset form state
      setValidationError(null)
      setLoading(false)
      setShowDeleteDialog(false)
      // Reset form data to initial state
      setFormData({
        name: goal.name,
        target_amount: goal.target_amount.toString(),
        description: goal.description || '',
        target_date: goal.target_date ? new Date(goal.target_date) : new Date()
      })
      
      requestAnimationFrame(() => {
        onOpenChange(newOpen)
      })
    } else {
      onOpenChange(newOpen)
    }
  }, [goal, onOpenChange])

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    try {
      setValidationError(null)
      setLoading(true)
      const amount = parseFloat(formData.target_amount)
      
      if (!formData.name.trim()) {
        throw new Error('Please enter a goal name')
      }
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Please enter a valid target amount')
      }
      if (formData.target_date < new Date()) {
        throw new Error('Please select a future date')
      }

      const selectedAccount = savingsAccounts.find(acc => acc.id === selectedAccountId)
      
      if (!selectedAccount) {
        throw new Error('Selected account not found')
      }

      await updateGoal(goal.id, {
        name: formData.name.trim(),
        target_amount: amount,
        target_date: formData.target_date.toISOString(),
        description: formData.description.trim() || null,
        account_id: selectedAccountId,
        currency: selectedAccount.currency || goal.currency
      })

      toast.success('Saving goal updated successfully!')
      handleOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update saving goal'
      setValidationError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }, [user, formData, goal.id, updateGoal, selectedAccountId, savingsAccounts])

  // Handle delete dialog state
  const handleDeleteDialogChange = useCallback((newOpen: boolean) => {
    setShowDeleteDialog(newOpen)
    if (!newOpen) {
      setLoading(false)
    }
  }, [])

  // Handle delete action
  const handleDelete = useCallback(async () => {
    if (!user || !goal.id) return

    try {
      setLoading(true)
      await deleteGoal(goal.id)
      toast.success('Saving goal deleted successfully!')
      handleOpenChange(false)
    } catch (error) {
      toast.error('Failed to delete saving goal')
    } finally {
      setLoading(false)
      handleDeleteDialogChange(false)
    }
  }, [user, goal.id, deleteGoal, handleOpenChange])

  return (
    <>
      <Dialog 
        open={open} 
        onOpenChange={handleOpenChange}
      >
        <DialogContent 
          className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl sm:max-w-[600px]"
          onPointerDownOutside={(e) => {
            if (!loading) {
              e.preventDefault()
              handleOpenChange(false)
            }
          }}
          onEscapeKeyDown={(e) => {
            if (!loading) {
              e.preventDefault()
              handleOpenChange(false)
            }
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogClose 
            asChild
            disabled={loading}
          >
            <button
              type="button"
              className="absolute right-4 top-4 rounded-sm text-text-secondary dark:text-text-dark-secondary opacity-70 hover:opacity-100 focus:outline-none disabled:pointer-events-none"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (!loading) {
                  handleOpenChange(false)
                }
              }}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </DialogClose>

          <DialogHeader className="space-y-2">
            <DialogTitle className="text-text-primary dark:text-text-dark-primary flex items-center text-2xl">
              <Target className="w-6 h-6 mr-2 text-secondary dark:text-primary-dark" />
              Edit Saving Goal
            </DialogTitle>
            <DialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              Update your saving goal details and track your progress.
            </DialogDescription>
          </DialogHeader>

          {validationError && (
            <Alert variant="destructive" className="border-negative/20 dark:border-negative/30 bg-negative/10 dark:bg-negative-dark/10">
              <AlertCircle className="h-4 w-4 text-negative dark:text-negative-dark" />
              <AlertDescription className="text-negative dark:text-negative-dark">{validationError}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-text-secondary dark:text-text-dark-secondary">Goal Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., New Car Fund"
                  className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="target_amount" className="text-text-secondary dark:text-text-dark-secondary">Target Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary dark:text-text-dark-secondary">
                    {currencySymbol}
                  </span>
                  <Input
                    id="target_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.target_amount}
                    onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
                    className="pl-7 bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_date" className="text-text-secondary dark:text-text-dark-secondary">Target Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="target_date"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark hover:bg-background-input dark:hover:bg-background-dark-input focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11",
                      !formData.target_date && "text-text-secondary dark:text-text-dark-secondary"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.target_date ? format(formData.target_date, "PPP") : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark rounded-xl">
                  <Calendar
                    mode="single"
                    selected={formData.target_date}
                    onSelect={(date) => date && setFormData({ ...formData, target_date: date })}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-text-secondary dark:text-text-dark-secondary">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Add more details about your goal..."
                className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="account" className="text-text-secondary dark:text-text-dark-secondary">Savings Account</Label>
              <Select
                value={selectedAccountId}
                onValueChange={setSelectedAccountId}
              >
                <SelectTrigger 
                  id="account" 
                  className="bg-background-input dark:bg-background-dark-input border-border dark:border-border-dark focus:border-secondary dark:focus:border-primary-dark rounded-xl h-11"
                >
                  <SelectValue placeholder="Select a savings account">
                    {savingsAccounts.find(acc => acc.id === selectedAccountId)?.name || "Select account"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border-border dark:border-border-dark rounded-xl">
                  {savingsAccounts.map((account) => (
                    <SelectItem 
                      key={account.id} 
                      value={account.id}
                      className="text-text-primary dark:text-text-dark-primary focus:bg-secondary/10 dark:focus:bg-primary-dark/10 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-secondary dark:text-primary-dark" />
                        <span>{account.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {suggestedSavings && (
              <Card className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-2 text-secondary dark:text-primary-dark">
                    <Calculator className="w-4 h-4" />
                    <span className="font-medium">Suggested Savings</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Daily</p>
                      <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                        {currencySymbol}{formatCurrency(suggestedSavings.daily, currencies.find(c => c.isDefault))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Weekly</p>
                      <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                        {currencySymbol}{formatCurrency(suggestedSavings.weekly, currencies.find(c => c.isDefault))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Monthly</p>
                      <p className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                        {currencySymbol}{formatCurrency(suggestedSavings.monthly, currencies.find(c => c.isDefault))}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between gap-3">
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={loading}
                className="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Goal
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={loading}
                  className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl h-11"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={handleDeleteDialogChange}>
        <AlertDialogContent className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-text-primary dark:text-text-dark-primary">Delete Goal</AlertDialogTitle>
            <AlertDialogDescription className="text-text-secondary dark:text-text-dark-secondary">
              Are you sure you want to delete this saving goal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              disabled={loading}
              className="bg-background-secondary/80 dark:bg-background-dark-secondary/80 backdrop-blur-lg backdrop-saturate-150 border-secondary dark:border-primary-dark text-secondary dark:text-primary-dark hover:bg-secondary/10 dark:hover:bg-primary-dark/10 rounded-xl h-11"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={loading}
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white transition-all duration-200 h-11 px-6 rounded-xl shadow-sm"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
